import {
  ScmIntegrationsApi,
  scmIntegrationsApiRef,
  ScmAuth,
} from '@backstage/integration-react';
import {
  AnyApiFactory,
  configApiRef,
  createApiFactory,
  discoveryApiRef,
  fetchApiRef,
  identityApiRef,
  storageApiRef,
  IdentityApi,
  FetchApi,
} from '@backstage/core-plugin-api';
import { CatalogClient } from '@backstage/catalog-client';
import { apiDocsConfigRef } from '@backstage/plugin-api-docs';
import { createApiDocsConfig } from './components/catalog/apiDocsConfig';

// Custom FetchApi that injects Bearer token from identityApi
class AuthenticatedFetchApi implements FetchApi {
  constructor(private readonly identityApi: IdentityApi) {}

  async fetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
    try {
      // Get credentials from identityApi
      const credentials = await this.identityApi.getCredentials();

      if (credentials?.token) {
        // Clone headers and add Authorization
        const headers = new Headers(init?.headers);
        if (!headers.has('Authorization')) {
          headers.set('Authorization', `Bearer ${credentials.token}`);
        }

        return fetch(input, {
          ...init,
          headers,
        });
      }
    } catch (error) {
      // If getting credentials fails, proceed without token
      console.warn('[AuthenticatedFetchApi] Failed to get credentials:', error);
    }

    // Fallback to fetch without token
    return fetch(input, init);
  }
}

import {
  catalogApiRef,
  starredEntitiesApiRef,
  entityPresentationApiRef,
} from '@backstage/plugin-catalog-react';
import { DefaultStarredEntitiesApi } from '@backstage/plugin-catalog';
import {
  IdentityPermissionApi,
  permissionApiRef,
} from '@backstage/plugin-permission-react';
import { JiraApi, jiraApiRef } from './components/jira/JiraApi';
import { techdocsStorageApiRef } from '@backstage/plugin-techdocs-react';
import { TechDocsStorageClient } from '@backstage/plugin-techdocs';

class NormalizedCatalogClient extends CatalogClient {
  async getEntities(request?: any) {
    const response = await super.getEntities(request);

    // Normalize nested structure {items: {items: Array}} to {items: Array}
    if (response && typeof response === 'object') {
      const resp = response as any;
      if (resp.items && resp.items.items && Array.isArray(resp.items.items)) {
        return {
          items: resp.items.items,
          pageInfo: resp.items.pageInfo,
          totalItems: resp.items.totalItems,
        };
      }
    }

    return response;
  }
}

export const apis: AnyApiFactory[] = [
  createApiFactory({
    api: apiDocsConfigRef,
    deps: {},
    factory: createApiDocsConfig,
  }),
  // Custom FetchApi that adds Bearer token to all requests
  createApiFactory({
    api: fetchApiRef,
    deps: { identityApi: identityApiRef },
    factory: ({ identityApi }) => new AuthenticatedFetchApi(identityApi),
  }),
  createApiFactory({
    api: scmIntegrationsApiRef,
    deps: { configApi: configApiRef },
    factory: ({ configApi }) => ScmIntegrationsApi.fromConfig(configApi),
  }),
  createApiFactory({
    api: catalogApiRef,
    deps: { discoveryApi: discoveryApiRef, fetchApi: fetchApiRef },
    factory: ({ discoveryApi, fetchApi }) =>
      new NormalizedCatalogClient({ discoveryApi, fetchApi }),
  }),
  createApiFactory({
    api: starredEntitiesApiRef,
    deps: { storageApi: storageApiRef },
    factory: ({ storageApi }) => new DefaultStarredEntitiesApi({ storageApi }),
  }),
  createApiFactory({
    api: entityPresentationApiRef,
    deps: { catalogApi: catalogApiRef },
    factory: ({ catalogApi }) => ({
      forEntity: entityOrRef => {
        const entityRef =
          typeof entityOrRef === 'string'
            ? entityOrRef
            : `${entityOrRef.kind}:${
                entityOrRef.metadata?.namespace || 'default'
              }/${entityOrRef.metadata?.name}`;

        return {
          snapshot: {
            entityRef,
            primaryTitle:
              typeof entityOrRef === 'string'
                ? entityOrRef
                : entityOrRef.metadata?.title || entityOrRef.metadata?.name,
            secondaryTitle:
              typeof entityOrRef === 'string'
                ? undefined
                : entityOrRef.metadata?.namespace,
            Icon: undefined,
          },
          promise: Promise.resolve({
            entityRef,
            primaryTitle:
              typeof entityOrRef === 'string'
                ? entityOrRef
                : entityOrRef.metadata?.title || entityOrRef.metadata?.name,
            secondaryTitle:
              typeof entityOrRef === 'string'
                ? undefined
                : entityOrRef.metadata?.namespace,
            Icon: undefined,
          }),
        };
      },
    }),
  }),
  createApiFactory({
    api: jiraApiRef,
    deps: {
      discoveryApi: discoveryApiRef,
      fetchApi: fetchApiRef,
      configApi: configApiRef,
    },
    factory: ({ discoveryApi, fetchApi, configApi }) =>
      new JiraApi(discoveryApi, fetchApi, configApi),
  }),
  createApiFactory({
    api: permissionApiRef,
    deps: {
      config: configApiRef,
      discovery: discoveryApiRef,
      identity: identityApiRef,
    },
    factory: ({ config, discovery, identity }) =>
      IdentityPermissionApi.create({ config, discovery, identity }),
  }),
  createApiFactory({
    api: techdocsStorageApiRef,
    deps: {
      configApi: configApiRef,
      discoveryApi: discoveryApiRef,
      identityApi: identityApiRef,
      fetchApi: fetchApiRef,
    },
    factory: ({ configApi, discoveryApi, identityApi, fetchApi }) =>
      new TechDocsStorageClient({
        configApi,
        discoveryApi,
        identityApi,
        fetchApi,
      }),
  }),
  ScmAuth.createDefaultApiFactory(),
];
